import React from "react";

import { createRole, deleteRole, duplicateRole, fetchAccessRoles, updateRole } from "../api/accessAdminClient";

export function RbacSettingsScreen(): JSX.Element {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [roles, setRoles] = React.useState<
    { id: string; name: string; description?: string | null; permissions: string[]; isSystem?: boolean }[]
  >([]);

  const loadRoles = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAccessRoles();
      setRoles(response.roles ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  const handleCreate = async () => {
    const name = window.prompt("Role name") ?? "";
    if (!name.trim()) return;
    const reason = window.prompt("Reason for creating role") ?? "";
    if (!reason.trim()) return;
    try {
      await createRole(name.trim(), null, [], reason);
      await loadRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create role");
    }
  };

  const handleDuplicate = async (roleId: string) => {
    const reason = window.prompt("Reason for duplicating role") ?? "";
    if (!reason.trim()) return;
    try {
      await duplicateRole(roleId, reason);
      await loadRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to duplicate role");
    }
  };

  const handleDelete = async (roleId: string) => {
    const reason = window.prompt("Reason for deleting role") ?? "";
    if (!reason.trim()) return;
    const confirmed = window.confirm("Delete this role?");
    if (!confirmed) return;
    try {
      await deleteRole(roleId, reason);
      await loadRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete role");
    }
  };

  const handleUpdatePermissions = async (roleId: string, permissions: string[]) => {
    const input = window.prompt("Comma-separated permission keys", permissions.join(", ")) ?? "";
    const nextPermissions = input
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
    const reason = window.prompt("Reason for updating permissions") ?? "";
    if (!reason.trim()) return;
    try {
      await updateRole(roleId, { permissions: nextPermissions }, reason);
      await loadRoles();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-600">Loading RBAC roles…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">RBAC roles</h2>
            <p className="text-sm text-slate-600">Manage custom roles and permissions.</p>
          </div>
          <button type="button" className="button button-primary" onClick={handleCreate}>
            Create role
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="rounded border border-slate-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          {roles.map((role) => (
            <div key={role.id} className="rounded border border-slate-100 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{role.name}</div>
                  {role.description ? <div className="text-xs text-slate-500">{role.description}</div> : null}
                </div>
                <div className="flex items-center gap-2">
                  {!role.isSystem ? (
                    <>
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() => handleDuplicate(role.id)}
                      >
                        Duplicate
                      </button>
                      <button
                        type="button"
                        className="button button-secondary"
                        onClick={() => handleDelete(role.id)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-500">System role</span>
                  )}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {role.permissions.map((permission) => (
                  <span key={permission} className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-700">
                    {permission}
                  </span>
                ))}
              </div>
              {!role.isSystem ? (
                <div className="mt-3 text-xs text-slate-500">
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => handleUpdatePermissions(role.id, role.permissions)}
                  >
                    Edit permissions
                  </button>
                </div>
              ) : null}
            </div>
          ))}
          {roles.length === 0 ? <div className="text-sm text-slate-500">No roles found.</div> : null}
        </div>
      </div>
    </div>
  );
}
